import html2pdf from "html2pdf.js";

const sanitize = (v) => {
  if (v === undefined || v === null) return "";
  const s = String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const NO_DATA_MESSAGE = "No data available. Download was not started.";

const showNoDataMessage = () => {
  if (typeof window !== "undefined") {
    window.alert(NO_DATA_MESSAGE);
  }
};

const hasNonEmptyRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((row) =>
    Array.isArray(row) &&
    row.some((cell) => String(cell ?? "").trim().length > 0)
  );
};

const getRestaurantMeta = () => {
  const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const name =
    data.restaurant_name || data.restaurantname || data.name || "Bill Sarthi";
  const address = [
    data.addressline1 || data.address || "",
    data.addressline2 || "",
    data.addressline3 || "",
    data.city || data.restaurant_city || "",
  ]
    .filter(Boolean)
    .join(", ");
  const phone =
    data.mobile_number ||
    data.mobile ||
    data.restaurantphone ||
    data.phone ||
    data.phonenumber ||
    "";
  return {
    name,
    address,
    phone,
    generatedOn: new Date().toLocaleString("en-IN"),
  };
};

const cleanExportClone = (root) => {
  if (!root) return;

  // Remove common interactive controls from export output.
  root
    .querySelectorAll(
      "button, input, select, textarea, [role='button'], .no-print, .no-export, [data-export-ignore='true']"
    )
    .forEach((el) => el.remove());

  // Important: do not aggressively remove "empty" nodes here.
  // Visual nodes like <img>, icon wrappers, and styled containers can appear
  // text-empty but are still required for html2canvas/pdf rendering.
};

const hasExportableDomData = (root) => {
  if (!root) return false;

  // Strong positive signal: table body has real cells.
  const dataCells = root.querySelectorAll("tbody td");
  if (dataCells.length > 0) {
    const hasValue = Array.from(dataCells).some(
      (td) => String(td.textContent || "").trim().length > 0
    );
    if (hasValue) return true;
  }

  const text = String(root.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  if (!text) return false;

  // Common empty-state messages used in reports.
  if (
    text.includes("no data") ||
    text.includes("no entries") ||
    text.includes("no records") ||
    text.includes("unable to load")
  ) {
    return false;
  }

  // Fallback signal for non-table reports (cards/charts/bars with text values).
  return text.length > 40;
};

const waitForCloneImages = async (root, timeoutMs = 5000) => {
  const images = Array.from(root?.querySelectorAll?.("img") || []);
  if (!images.length) return;

  await Promise.all(
    images.map((img) => {
      const src = String(img.getAttribute("src") || img.src || "");
      if (!src) return Promise.resolve();

      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const probe = new window.Image();
        const done = () => resolve();
        const isRemoteHttp = /^https?:\/\//i.test(src);
        if (isRemoteHttp) {
          probe.crossOrigin = "anonymous";
          img.crossOrigin = "anonymous";
        }
        probe.onload = done;
        probe.onerror = done;
        probe.src = src;
        window.setTimeout(done, timeoutMs);
      });
    })
  );
};

export const downloadCSV = (filename, headers = [], rows = []) => {
  if (!hasNonEmptyRows(rows)) {
    showNoDataMessage();
    return false;
  }

  const meta = getRestaurantMeta();
  const prefixedRows =
    Array.isArray(headers) && headers.length
      ? [
          [`Restaurant: ${meta.name}`],
          [`Address: ${meta.address || "-"}`],
          [`Generated On: ${meta.generatedOn}`],
          [],
          headers,
          ...rows,
          [],
          ["Auto-generated with Bill Sarthi"],
        ]
      : [headers, ...rows];
  const csv = prefixedRows
    .map((row) => row.map(sanitize).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};

export const downloadPDF = async (element, filename) => {
  if (!element) return;
  const meta = getRestaurantMeta();
  const contentClone = element.cloneNode(true);
  cleanExportClone(contentClone);
  if (!hasExportableDomData(contentClone)) {
    showNoDataMessage();
    return false;
  }

  const root = document.createElement("div");
  root.style.background = "#ffffff";
  root.style.padding = "0";
  root.style.color = "#111827";
  root.style.fontFamily = "Arial, Helvetica, sans-serif";

  const header = document.createElement("div");
  header.style.background = "linear-gradient(135deg, #015b9a, #003962)";
  header.style.color = "#ffffff";
  header.style.padding = "10px 12px";
  header.style.borderRadius = "10px";
  header.style.marginBottom = "10px";
  header.innerHTML = `
    <div style="font-size:18px;font-weight:800;">${meta.name}</div>
    <div style="font-size:12px;opacity:0.95;margin-top:2px;">${meta.address || "-"}</div>
    <div style="font-size:12px;opacity:0.95;margin-top:2px;">${meta.phone ? `Phone: ${meta.phone}` : ""}</div>
    <div style="font-size:11px;opacity:0.85;margin-top:6px;">Generated On: ${meta.generatedOn}</div>
  `;

  const bodyWrap = document.createElement("div");
  bodyWrap.className = "bs-export-body";
  bodyWrap.style.border = "1px solid #e5e7eb";
  bodyWrap.style.borderRadius = "10px";
  bodyWrap.style.padding = "10px";
  bodyWrap.style.background = "#ffffff";
  bodyWrap.appendChild(contentClone);

  const footer = document.createElement("div");
  footer.style.marginTop = "10px";
  footer.style.paddingTop = "8px";
  footer.style.borderTop = "1px solid #e5e7eb";
  footer.style.fontSize = "12px";
  footer.style.color = "#475569";
  footer.style.textAlign = "center";
  footer.textContent = "Auto-generated with Bill Sarthi";

  root.appendChild(header);
  root.appendChild(bodyWrap);
  root.appendChild(footer);

  await waitForCloneImages(root);

  const opt = {
    margin: 10,
    filename,
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: "#ffffff",
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };
  await html2pdf().set(opt).from(root).save();
  return true;
};

const normalizePrinterWidth = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "a4" || raw === "a4-size" || raw === "a4size") {
    return "a4";
  }
  if (
    raw === "4" ||
    raw === "4in" ||
    raw === "4inch" ||
    raw === "4-inch" ||
    raw === "4 in" ||
    raw === "112mm" ||
    raw === "104mm" ||
    raw === "102mm" ||
    raw === "100mm"
  ) {
    return "4in";
  }
  if (
    raw === "3" ||
    raw === "3in" ||
    raw === "3inch" ||
    raw === "3-inch" ||
    raw === "80mm" ||
    raw === "76mm" ||
    raw === "72mm" ||
    raw === "80"
  ) {
    return "3in";
  }
  return "2in";
};

const getThermalPaper = () => {
  const saved =
    localStorage.getItem("printerWidth") || localStorage.getItem("printer_width");
  const width = normalizePrinterWidth(saved);
  if (width === "a4") {
    return { key: "a4", mm: null, px: 1100, fontSize: 13, size: "A4", margin: "10mm" };
  }
  if (width === "4in") {
    return { key: "4in", mm: 104, px: 740, fontSize: 12, size: "104mm auto", margin: "4mm" };
  }
  return width === "3in"
    ? { key: "3in", mm: 80, px: 576, fontSize: 12, size: "80mm auto", margin: "4mm" }
    : { key: "2in", mm: 58, px: 420, fontSize: 11, size: "58mm auto", margin: "4mm" };
};

const ESC = 0x1b;
const GS = 0x1d;
const INIT_CMD = new Uint8Array([ESC, 0x40]);
const CUT_CMD = new Uint8Array([GS, 0x56, 0x00]);
const ALIGN_LEFT = new Uint8Array([ESC, 0x61, 0x00]);

const BLE_OPTIONAL_SERVICES = [
  0xffe0,
  0xffe1,
  0xffe5,
  0x18f0,
  0x18f1,
  0xff00,
  0xff01,
  0xff02,
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "000018f1-0000-1000-8000-00805f9b34fb",
];

const BLE_KNOWN_SERVICES = [
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "000018f1-0000-1000-8000-00805f9b34fb",
];

const BLE_KNOWN_CHARS = [
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
];

const BLE_STATE = {
  characteristic: null,
  deviceId: "",
};

const chunkBytes = (bytes, size = 20) => {
  const out = [];
  for (let i = 0; i < bytes.length; i += size) out.push(bytes.slice(i, i + size));
  return out;
};

const centerLine = (text = "", width = 32) => {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length >= width) return s.slice(0, width);
  const gap = width - s.length;
  const left = Math.floor(gap / 2);
  return " ".repeat(left) + s + " ".repeat(gap - left);
};

const padR = (txt, w) => {
  const s = String(txt ?? "");
  return (s + " ".repeat(Math.max(0, w - s.length))).slice(0, w);
};

const wrapText = (text, width) => {
  const s = String(text || "");
  if (!s.trim()) return [""];
  const out = [];
  let rem = s.trim();
  while (rem.length) {
    if (rem.length <= width) {
      out.push(rem);
      break;
    }
    const slice = rem.slice(0, width + 1);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      out.push(slice.slice(0, lastSpace));
      rem = rem.slice(lastSpace + 1);
    } else {
      out.push(rem.slice(0, width));
      rem = rem.slice(width);
    }
    rem = rem.trimStart();
  }
  return out.length ? out : [""];
};

const findWritableCharacteristic = async (server, device) => {
  const services = await server.getPrimaryServices();
  for (const svc of services) {
    const chars = await svc.getCharacteristics();
    for (const c of chars) {
      const { write, writeWithoutResponse } = c.properties || {};
      if (write || writeWithoutResponse) {
        BLE_STATE.characteristic = c;
        BLE_STATE.deviceId = device?.id || "";
        if (device?.id) localStorage.setItem("ble_last_device_id", device.id);
        return c;
      }
    }
  }
  return null;
};

const findWritableCharacteristicFallback = async (server, device) => {
  for (const sid of BLE_KNOWN_SERVICES) {
    try {
      const svc = await server.getPrimaryService(sid);
      const chars = await svc.getCharacteristics();
      for (const c of chars) {
        const { write, writeWithoutResponse } = c.properties || {};
        if (write || writeWithoutResponse) {
          BLE_STATE.characteristic = c;
          BLE_STATE.deviceId = device?.id || "";
          if (device?.id) localStorage.setItem("ble_last_device_id", device.id);
          return c;
        }
      }
      for (const cid of BLE_KNOWN_CHARS) {
        try {
          const c = await svc.getCharacteristic(cid);
          const { write, writeWithoutResponse } = c.properties || {};
          if (write || writeWithoutResponse) {
            BLE_STATE.characteristic = c;
            BLE_STATE.deviceId = device?.id || "";
            if (device?.id) localStorage.setItem("ble_last_device_id", device.id);
            return c;
          }
        } catch {
          // continue
        }
      }
    } catch {
      // continue
    }
  }
  return null;
};

const connectBlePrinter = async () => {
  if (!navigator?.bluetooth) throw new Error("Web Bluetooth not supported");

  if (BLE_STATE.characteristic) return BLE_STATE.characteristic;

  if (navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      const preferredId = localStorage.getItem("ble_last_device_id") || BLE_STATE.deviceId;
      const device = preferredId
        ? devices.find((d) => d.id === preferredId)
        : devices[0];
      if (device) {
        const server = device.gatt?.connected ? device.gatt : await device.gatt.connect();
        let c = await findWritableCharacteristic(server, device);
        if (!c) c = await findWritableCharacteristicFallback(server, device);
        if (c) return c;
      }
    } catch {
      // proceed to manual picker
    }
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: BLE_OPTIONAL_SERVICES,
  });
  const server = await device.gatt.connect();
  let c = await findWritableCharacteristic(server, device);
  if (!c) c = await findWritableCharacteristicFallback(server, device);
  if (!c) throw new Error("No writable Bluetooth characteristic found");
  return c;
};

const writeBle = async (bytes) => {
  const c = BLE_STATE.characteristic || (await connectBlePrinter());
  const parts = chunkBytes(bytes, 20);
  const supportsFastWrite = !!c.writeValueWithoutResponse;
  for (const part of parts) {
    if (supportsFastWrite) await c.writeValueWithoutResponse(part);
    else await c.writeValue(part);
    if (!supportsFastWrite) {
      await new Promise((r) => setTimeout(r, 2));
    }
  }
};

const buildRowLines = (cells, colW) => {
  const wrapped = cells.map((cell) => wrapText(cell, colW));
  const maxLines = wrapped.reduce((m, arr) => Math.max(m, arr.length), 1);
  const out = [];
  for (let i = 0; i < maxLines; i += 1) {
    const line = wrapped.map((arr) => padR(arr[i] || "", colW)).join(" | ");
    out.push(line);
  }
  return out;
};

const buildReportReceiptText = (element, title, lineWidth) => {
  const lines = [];
  const userData = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const restaurant = userData.restaurant_name || userData.name || "Bill Sarthi";
  const headerTitle =
    title ||
    element?.querySelector?.("h1,h2,h3")?.textContent?.trim() ||
    "Report";

  lines.push(centerLine(restaurant, lineWidth));
  lines.push(centerLine(headerTitle, lineWidth));
  lines.push(centerLine(new Date().toLocaleString("en-IN"), lineWidth));
  lines.push("-".repeat(lineWidth));

  const tables = Array.from(element?.querySelectorAll?.("table") || []);
  if (!tables.length) {
    const raw = (element?.innerText || "")
      .split("\n")
      .map((t) => t.trim())
      .filter(Boolean)
      .filter(
        (t) =>
          !/^(refresh|pdf|csv|thermal print)$/i.test(t) &&
          !/^from:?$/i.test(t) &&
          !/^to:?$/i.test(t)
      )
      .slice(0, 120);
    raw.forEach((r) => wrapText(r, lineWidth).forEach((w) => lines.push(w)));
    return lines.join("\n");
  }

  tables.forEach((table, index) => {
    if (index > 0) lines.push("-".repeat(lineWidth));
    const th = Array.from(table.querySelectorAll("thead th")).map((n) =>
      (n.textContent || "").trim()
    );
    const bodyRows = Array.from(table.querySelectorAll("tbody tr")).map((tr) =>
      Array.from(tr.querySelectorAll("td")).map((n) => (n.textContent || "").trim())
    );
    const colCount = Math.max(th.length, bodyRows[0]?.length || 0);
    if (!colCount) return;
    const totalSep = (colCount - 1) * 3;
    const colW = Math.max(4, Math.floor((lineWidth - totalSep) / colCount));

    if (th.length) {
      buildRowLines(th, colW).forEach((ln) => lines.push(ln.slice(0, lineWidth)));
      lines.push("-".repeat(lineWidth));
    }
    bodyRows.forEach((row) => {
      const safe = row.slice(0, colCount);
      while (safe.length < colCount) safe.push("");
      buildRowLines(safe, colW).forEach((ln) => lines.push(ln.slice(0, lineWidth)));
    });
  });

  return lines.join("\n");
};

const fallbackBrowserThermalPrint = (element, title = "Thermal Report") => {
  if (!element) return;
  const paper = getThermalPaper();
  const printableHtml = element.innerHTML;
  const popup = window.open("", "_blank", `width=${paper.px},height=900`);
  if (!popup) return;

  popup.document.open();
  popup.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>
          @page { size: ${paper.size || `${paper.mm}mm auto`}; margin: ${paper.margin || "2mm"}; }
          html, body {
            margin: 0;
            padding: 0;
            width: ${paper.key === "a4" ? "auto" : `${paper.mm}mm`};
            max-width: ${paper.key === "a4" ? "190mm" : `${paper.mm}mm`};
            font-family: Arial, Helvetica, sans-serif;
            font-size: ${paper.fontSize}px;
            color: #111827;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: #fff !important;
          }
          * { box-sizing: border-box; }
          .no-print, button, input, select, textarea { display: none !important; }
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            table-layout: fixed !important;
          }
          th, td {
            border: 1px solid #d1d5db !important;
            padding: 4px 3px !important;
            vertical-align: top !important;
            word-break: break-word !important;
            white-space: normal !important;
          }
          th { background: #f3f4f6 !important; }
          h1, h2, h3, h4, h5, h6 {
            margin: 0 0 6px !important;
            line-height: 1.2 !important;
          }
          p, div, span { line-height: 1.25 !important; }
        </style>
      </head>
      <body>
        <div>${printableHtml}</div>
        <script>
          window.onload = function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 120);
          };
        </script>
      </body>
    </html>
  `);
  popup.document.close();
};

export const printThermal = async (element, title = "Thermal Report") => {
  if (!element) return;
  const paper = getThermalPaper();
  const lineWidth =
    paper.key === "a4" ? 72 : paper.key === "4in" ? 64 : paper.key === "3in" ? 48 : 32;
  const receiptText = buildReportReceiptText(element, title, lineWidth);
  const enc = new TextEncoder();
  const chunks = [
    INIT_CMD,
    ALIGN_LEFT,
    enc.encode(receiptText + "\n\n\n"),
    CUT_CMD,
  ];
  const totalLen = chunks.reduce((sum, arr) => sum + arr.length, 0);
  const payload = new Uint8Array(totalLen);
  let offset = 0;
  chunks.forEach((arr) => {
    payload.set(arr, offset);
    offset += arr.length;
  });

  try {
    await writeBle(payload);
  } catch (error) {
    fallbackBrowserThermalPrint(element, title);
  }
};
